import type { JavaScriptFunctionState } from '@unifyapps/defs/types/javascriptFunction';
import type { JavaScriptVariableState } from '@unifyapps/defs/types/javascriptVariable';
import { _get } from '../../../immutable';
import { INTERNALS_PARAMETERS_KEY } from '../../const';
import type {
  DataSourceMetaType,
  QueryRequestResult,
} from '../../slices/DataSourceControllerSlice/types';
import type { GlobalAppStateStoreType } from '../../stores/app/GlobalAppStateStore';
import { ComputeContextKeys } from './const';
import type { ComputeContextType } from './context/types';

/**
 * ComputeContextHelper is a helper class to get the value of a context items from their location in the context.
 * It is necessary because every block is given a context that is basically the context that was used to compute the block.
 * So any computation or decisions should be taken based on this context.
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class -- need it
export class ComputeContextHelper {
  static getSessionStorage(context: ComputeContextType) {
    return context[ComputeContextKeys.SESSION_STORAGE] as
      | GlobalAppStateStoreType['sessionStorage']['values']
      | undefined;
  }

  static getDataSourceQueryResult(context: ComputeContextType, dataSourceId: string) {
    return context[dataSourceId] as QueryRequestResult | undefined;
  }

  static getDataSourceMeta(context: ComputeContextType, dataSourceId: string) {
    return context.dataSourcesMeta?.[dataSourceId] as DataSourceMetaType | undefined;
  }

  static getBlockState<T>(context: ComputeContextType, blockId: string) {
    return context[blockId] as T | undefined;
  }

  static getPageVariableState(context: ComputeContextType, pageVariableId: string) {
    return context[pageVariableId] as JavaScriptVariableState | undefined;
  }

  static getUserContext(context: ComputeContextType) {
    return context[ComputeContextKeys.USER_CONTEXT] as
      | GlobalAppStateStoreType['userContext']
      | undefined;
  }

  static getPageInputs<T>(context: ComputeContextType) {
    return context[ComputeContextKeys.PAGE_INPUTS] as T | undefined;
  }

  static getPageFunctionState(context: ComputeContextType, pageFunctionId: string) {
    return context[pageFunctionId] as JavaScriptFunctionState | undefined;
  }

  static getBlockSessionState<T>({
    context,
    blockId,
    interfacePageId,
  }: {
    context: ComputeContextType;
    blockId: string;
    interfacePageId: string;
  }) {
    return _get(context[ComputeContextKeys.SESSION_STORAGE], [
      INTERNALS_PARAMETERS_KEY,
      interfacePageId,
      blockId,
    ]) as T | undefined;
  }
}
